import {
  CSSProperties,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, Text, Dialog, Icon, Dropdown } from '@/ui'
import { faDownload, faUpload } from '@fortawesome/pro-regular-svg-icons'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { TypographyStyles, TextColors } from '@/styles'

// BL
import { getUserBooksLockedForUserYear } from '@/reducers/auth/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { getSpreadsheetRowErrors, processCsv } from './helpers'

// UI
import { UploadScreenProps } from './index'
import { UploadMessage } from '../UploadMessage'
import { UPLOAD_COPY } from '@/features/transactions/copyConstants'
import { LockedBooksMessage } from '@/features/transactions/LockedBooksMessage'
import { SeparateStep } from '@/features/onboarding/SeparateTransactions'

export interface UploadCSVErrorModel {
  title: string
  body: Array<string>
}

export const EMPTY_TRANSACTION_CSV_DOWNLOAD =
  'https://docs.google.com/spreadsheets/d/1N-DJkEwMO-z_ucebhRKnZjnMOv2IEzu2TAfsn9nVmQI/export?format=csv'
export const EMPTY_TRANSACTION_CSV_COPY =
  'https://docs.google.com/spreadsheets/d/1N-DJkEwMO-z_ucebhRKnZjnMOv2IEzu2TAfsn9nVmQI/copy'

export const generateValidationErrorModel = ({
  errors,
}: {
  errors: string[]
}) => ({
  title: 'An error occurred when attempting to record your transactions!',
  body: errors,
})

export const ValidationErrorMessageContainer = ({
  error,
  style,
}: {
  error: UploadCSVErrorModel
  style?: CSSProperties
}) => (
  <div style={style}>
    <UploadMessage success={false} content={error.title} />
    <span>
      <ul className="w-[80%] text-left m-2">
        {error.body.map((e) => (
          <li key={`csv-upload-error-${e}`}>
            <Text>{e}</Text>
          </li>
        ))}
      </ul>
    </span>
  </div>
)

const UploadCSVPanel = ({ goToScreenWithData }: UploadScreenProps) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<UploadCSVErrorModel | null>(null)
  const [selectedFile, setSelectedFile] = useState<File>()

  const uploadFileRef = useRef<HTMLInputElement>(null)

  /**
   * Validate spreadsheet structure only on the client. Cell data not validated on this screen
   *
   * 1. Do 2+ rows exists?
   * 2. Does each row have exactly 3 columns?
   * 3. Does the first row have correct header names?
   */
  const validateSpreadsheetRows = (rows: string[][]) => {
    const errors = getSpreadsheetRowErrors(rows)

    if (errors.length > 0) {
      const model = generateValidationErrorModel({ errors })
      setError(model)
    } else {
      setError(null)
    }
    return errors.length === 0
  }

  useEffect(() => {
    // Extract data, validate it, navigate to review screen
    const processData = (csv: string) => {
      const rows = processCsv(csv)
      const valid = validateSpreadsheetRows(rows)

      if (valid) {
        // remove header row
        rows.splice(0, 1)

        const models = rows.map((r) => ({
          date: r[0],
          description: r[1],
          amountInDollars: r[2],
        }))

        goToScreenWithData({
          index: 2,
          data: { models, spreadsheetFile: selectedFile },
        })
      }

      setLoading(false)
    }

    const fileLoaded = (e: ProgressEvent<FileReader>) => {
      if (e.target) {
        const csv = e.target.result
        if (typeof csv === 'string') {
          processData(csv)
        }
      }
    }

    const fileError = (e: ProgressEvent<FileReader>) => {
      const errors = []

      if (e.target?.error?.name === 'NotReadableError') {
        errors.push('The file is not readable')
      } else {
        errors.push(`An error occurred. ${e.target?.error?.message || ''}`)
      }

      const model = generateValidationErrorModel({ errors })
      setError(model)
    }

    const getFileAsText = (file: File) => {
      const reader = new FileReader()
      // Read into memory UTF-8
      reader.readAsText(file)
      reader.onload = fileLoaded
      reader.onerror = fileError
    }
    if (selectedFile) {
      setLoading(true)
      getFileAsText(selectedFile)
    }
  }, [selectedFile, goToScreenWithData])

  const onGoogleDriveClick = (e: SyntheticEvent) => {
    e.preventDefault()
    window.open(EMPTY_TRANSACTION_CSV_COPY, '_blank')?.focus()
    return false
  }

  const onDownloadFileClick = (e: SyntheticEvent) => {
    e.preventDefault()
    window.open(EMPTY_TRANSACTION_CSV_DOWNLOAD, '_blank')?.focus()
    return false
  }

  return (
    <>
      <Text variant={TypographyStyles.H2}>Add Multiple Transactions</Text>
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        Follow these instructions to bulk add transactions
      </Text>
      <SeparateStep
        stepNumber="1"
        title="Download transaction spreadsheet"
        content={
          <Dropdown>
            <div className="h-4" />
            <Dropdown.Trigger>
              <Button variant="secondary" size="sm" iconLeft={faDownload}>
                Download Template
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Content className="z-[10000]">
              <Dropdown.Item
                onClick={onDownloadFileClick}
                className="flex flex-col"
              >
                <Text
                  variant={TypographyStyles.LABEL_MD}
                  className="w-full m-0"
                >
                  Download a file
                </Text>
              </Dropdown.Item>
              <Dropdown.Item
                onClick={onGoogleDriveClick}
                className="flex flex-col"
              >
                <Text variant={TypographyStyles.LABEL_MD}>
                  Copy to Google Drive
                </Text>
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown>
        }
      />
      <SeparateStep
        stepNumber="2"
        title="Enter your transactions in the spreadsheet"
        content={
          <ul className="ml-8 mt-4 list-disc">
            <li>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Fill in your transactions dating from January of this year to
                the current date
              </Text>
            </li>
            <li>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Use this format: date, description, dollar amount
              </Text>
            </li>
            <li>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Mark <b>(+) for income/deposit</b> or <b>(-) for expense</b>{' '}
                next to the dollar amount
              </Text>
            </li>
            <li>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Refer to the <b>Instructions</b> tab for more details{' '}
              </Text>
            </li>
            <li>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Save this file as a <strong>.csv</strong>
              </Text>
            </li>
          </ul>
        }
      />
      <SeparateStep
        stepNumber="3"
        title="Upload the completed spreadsheet"
        content={
          <>
            <div className="h-4" />
            <Button
              className="mt-3"
              disabled={loading}
              onClick={() => uploadFileRef.current?.click()}
              iconLeft={faUpload}
              size="sm"
            >
              Upload Spreadsheet
            </Button>

            <input
              ref={uploadFileRef}
              accept=".csv"
              type="file"
              hidden
              onChange={(e) => {
                if (e.target.files) {
                  setSelectedFile(e.target.files[0])
                }
              }}
            />
            {error && (
              <ValidationErrorMessageContainer
                style={{ margin: '1em 1em 0 1em', width: '100%' }}
                error={error}
              />
            )}
          </>
        }
      />
    </>
  )
}

export const UploadCSVModal = (props: UploadScreenProps) => {
  const booksLockedForUserYear = useReselector(getUserBooksLockedForUserYear)

  return (
    <>
      <Dialog.Title>{UPLOAD_COPY.GENERIC_SCREEN_TITLE}</Dialog.Title>

      <Dialog.Content>
        <div className="flex flex-col">
          {booksLockedForUserYear && (
            <div className="w-full">
              <LockedBooksMessage year={booksLockedForUserYear} />
            </div>
          )}
          <UploadCSVPanel {...props} />
        </div>
      </Dialog.Content>
      <Dialog.Close>
        <Icon name="times" icon={faTimes} />
      </Dialog.Close>
    </>
  )
}
